import Modal from "../../Unknown/Modal";
import { useAppDispatch, useAppSelector } from "../../../modules/store/hooks";
import { modalsSlice } from "../../../modules/store/reducers/modalsSlice";
import { Box, Button, TextField } from "@mui/material";
import translate from "../../../i18n/translate";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useMemo, useState } from "react";
import dayjs from "dayjs";
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import toast from "react-hot-toast";
import { eventApi } from "../../../modules/services/eventService";
import download from "../../../common/download";
import format from "date-fns/format";

export default function DownloadReportModal() {
  const dispatch = useAppDispatch();
  const { toggleDownloadReportModal } = modalsSlice.actions;
  const visible = useAppSelector((state) => state.modalsReducer.downloadReport);

  const [dateFrom, setDateFrom] = useState<any>(new Date());
  const [dateTo, setDateTo] = useState<any>(new Date());
  const [ignoreYeshInvoice, setIgnoreYeshInvoice] = useState<boolean>(true);
  const [onlyUnpaidEvents, setOnlyUnpaidEvents] = useState<boolean>(false);
  const [ignoreZCredit, setIgnoreZCredit] = useState<boolean>(false);
  const [ignoreTransactions, setIgnoreTransactions] = useState<boolean>(false);
  const [debug, setDebug] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  const handleSubmit = async () => {
    try {
      setIsLoading(true);
      const startDateApi = format(new Date(dateFrom), 'MM-dd-yyyy');
      const endDateApi = format(new Date(dateTo), 'MM-dd-yyyy');

      const response = await dispatch(eventApi.endpoints.downloadReport.initiate({ startDate: startDateApi, endDate: endDateApi, onlyUnpaidEvent: onlyUnpaidEvents, ignoreYeshInvoice, ignoreZCredit, ignoreTransactions, debug }))

      const startDateFile = format(new Date(dateFrom), 'dd-MM-yyyy');
      const endDateFile = format(new Date(dateTo), 'dd-MM-yyyy');
      const fileName = `report_events_${startDateFile}_${endDateFile}${debug ? '_debug' : ''}.xlsx`;

      download(
        response.data,
        fileName,
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      )

      dispatch(toggleDownloadReportModal());
    } catch (error) {
      toast.error(translate('downloadReportError'));
    } finally {
      setIsLoading(false);
    }
  };

  const onCloseModal = () => {
    dispatch(toggleDownloadReportModal());
  }

  const isDisabled = useMemo(() => {
    if (!dateFrom && !dateTo) {
      return true;
    }

    const from = dayjs(dateFrom)
    const to = dayjs(dateTo)

    if (from.isSame(to)) {
      return false
    }

    if (from.isAfter(to)) {
      return true
    }

    if (from.isBefore(to)) {
      return false;
    }

    return true;
  }, [dateFrom, dateTo])

  const onChangeOnlyUnpaidEvents = () => {
    setOnlyUnpaidEvents((prev) => !prev);

    if (!onlyUnpaidEvents) {
      setIgnoreYeshInvoice(true);
      setIgnoreZCredit(true);
      setIgnoreTransactions(true);
    }
  }

  const onChangeSomeFlags = (setFlag: any) => {
    setFlag();
    console.log(onlyUnpaidEvents);

    if (onlyUnpaidEvents) {
      setOnlyUnpaidEvents(false);
    }
  }

  return (
    <Modal
      title={translate("downloadReportModalTitle")}
      visible={visible === "open"}
      style={{ maxWidth: "500px" }}
      setVisible={onCloseModal}
    >
      <Box my={2} p={2} display="flex" flexDirection="column" justifyContent="center" width={1} alignItems="center" gap={2}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label={translate('downloadReportFromDate')}
            value={dateFrom}
            onChange={(value) => {
              const date=dayjs(value,'MM-DD-YYYY')
              setDateFrom(date)
            }}
            mask="__/__/____"
            renderInput={(params) => 
              <TextField {...params} 
                dir="rtl"
                value={dateFrom}
                onChange={(event) => {
                  const date = dayjs(event.target.value,'MM-DD-YYYY')
                  setDateFrom(date)
                }}
                size="small"
                sx={{
                  marginTop: '5px',
                  width: "200px",
                  marginRight: "10px"
                }} 
                color="warning"
              />
            }
          />
          <DatePicker
            label={translate('downloadReportToDate')}
            value={dateTo}
            onChange={(value) => {
              const date=dayjs(value,'MM-DD-YYYY')
              setDateTo(date)
            }}
            mask="__/__/____"
            renderInput={(params) => 
              <TextField {...params} 
                value={dateTo}
                dir="rtl"
                color="warning"
                onChange={(event) => {
                  const date=dayjs(event.target.value, 'MM-DD-YYYY')
                  setDateTo(date)
                }}
                size="small"
                sx={{
                  marginTop: '15px',
                  width: "200px",
                  marginRight: "10px"
                }} 
              />
            }
          />
        </LocalizationProvider>
        <Button 
          sx={{
            width: '300px'
          }}
          color="warning"
          startIcon={
            ignoreZCredit 
            ? <CheckBoxIcon style={{ marginRight: "10px" }}/>
            : <CheckBoxOutlineBlankIcon style={{ marginRight: "10px" }}/>
          }
          onClick={() => onChangeSomeFlags(() => setIgnoreZCredit((prev) => !prev))}
        >
          {translate("ignoreZCreditText")}
        </Button>
        <Button 
          sx={{
            width: '300px'
          }}
          color="warning"
          startIcon={
            ignoreTransactions 
            ? <CheckBoxIcon style={{ marginRight: "10px" }}/>
            : <CheckBoxOutlineBlankIcon style={{ marginRight: "10px" }}/>
          }
          onClick={() => onChangeSomeFlags(() => setIgnoreTransactions((prev) => !prev))}
        >
          {translate("ignoreTransactionsText")}
        </Button>
        <Button 
          sx={{
            width: '300px'
          }}
          color="warning"
          startIcon={
            ignoreYeshInvoice 
            ? <CheckBoxIcon style={{ marginRight: "10px" }}/>
            : <CheckBoxOutlineBlankIcon style={{ marginRight: "10px" }}/>
          }
          onClick={() => onChangeSomeFlags(() => setIgnoreYeshInvoice((prev) => !prev))}
        >
          {translate("ignoreYeshInvoiceText")}
        </Button>
        <Button
          sx={{
            width: '300px'
          }}
          color="warning"
          startIcon={
            onlyUnpaidEvents
            ? <CheckBoxIcon style={{ marginRight: "10px" }}/>
            : <CheckBoxOutlineBlankIcon style={{ marginRight: "10px" }}/>
          }
          onClick={onChangeOnlyUnpaidEvents}
        >
          {translate("onlyUnpaidEventsFlagText")}
        </Button>
        <Button
          sx={{
            width: '300px'
          }}
          color="warning"
          startIcon={
            debug
            ? <CheckBoxIcon style={{ marginRight: "10px" }}/>
            : <CheckBoxOutlineBlankIcon style={{ marginRight: "10px" }}/>
          }
          onClick={() => setDebug((prev) => !prev)}
        >
          Debug (include guest breakdown)
        </Button>
        <Button
          size="large"
          variant="contained"
          color="warning"
          fullWidth
          onClick={handleSubmit}
          disabled={isDisabled || isLoading}
        >
          {translate("downloadReport")}
        </Button>
      </Box>
    </Modal>
  );
}
